/**
 * The campaigns that have stopped themselves and are waiting for a person.
 *
 * **This is the most urgent thing this screen can say, so it is the first thing
 * on it.** Slice 18 made a campaign able to halt its own send because of how its
 * messages were being received — too many addresses that did not exist, too many
 * numbers that could not be reached, or no delivery reports at all to judge by —
 * and a halt is a send that has stopped part-way through a list with nobody
 * told. The credit pause is the same shape from an older cause: `paused_
 * insufficient_credits` predates slice 18 and stops for a reason a person has to
 * resolve.
 *
 * ## EMPTY IS THE NORMAL CASE AND RENDERS NOTHING
 *
 * Not "All campaigns running", not a green tick, not a card saying there is
 * nothing to report. A reassurance tile occupying the most valuable space on the
 * screen on the 364 days it has nothing to say is how a reader learns to skip
 * that space on the day it does. `total === 0` renders `null`, and the layout
 * above it owns the gap that goes with it.
 *
 * ## WHY IT IS NOT THE RECENT-CAMPAIGNS LIST WITH A BADGE
 *
 * That list is the last five campaigns by recency. A campaign halted three weeks
 * ago has already fallen off it, and the whole point of a halt is that it is
 * still stopped. So `overviewAttentionSchema` is its own read, ordered by when
 * the stop happened, capped at {@link OVERVIEW_ATTENTION_LIMIT} with an uncapped
 * `total` beside it — because a truncated total is a wrong total, and a block
 * that derived the count from `campaigns.length` would say four when eleven
 * campaigns are stopped.
 *
 * ## THE SENTENCE IS SHORT AND IT IS NOT SLICE 18'S
 *
 * `CAMPAIGN_HALT_SENTENCES` are three to four lines each and end with what to do
 * about it. That is right on the campaign screen, where the reader has arrived to
 * deal with this one campaign; four of them stacked on a dashboard is a wall
 * nobody reads. `OVERVIEW_ATTENTION_SUMMARIES` is the four-word version, carried
 * on the row from the same `reason` code, and the link goes to the campaign,
 * which prints the long sentence. Progressive, not deleted — nothing on this
 * block is the only place a fact appears.
 *
 * Every word here comes from the contract. Nothing in this file writes a
 * sentence about a halt: a fifth halt reason added in `list-safety.ts` arrives
 * with its own summary through `stoppedCampaign`, rather than rendering as a
 * blank on the one block whose job is to be noticed.
 *
 * ## NO COUNTS ON A ROW, DELIBERATELY
 *
 * The two numbers a reader would want — how many failed, out of how many — are
 * on the campaign screen with the volume caveat beside them. A bare "3 of 51" on
 * a dashboard row is the percentage-from-a-tiny-denominator problem with the
 * division left to the reader, on the screen that refuses to print such a
 * percentage itself.
 *
 * ## TONE
 *
 * `bad`, not `warn`. A campaign that stopped itself over delivery risk is the
 * one state on this product that is *already* costing something: the list is
 * half-sent and the reason it stopped is the reason the AWS rates at the top of
 * this screen exist. The block carries `role="alert"`-adjacent weight by
 * position rather than by ARIA — it is present on load rather than announced
 * mid-session, and a live region for something that was there when the page
 * opened interrupts a screen-reader user reading the heading.
 *
 * ## WHEN THE READ THAT CARRIES IT FAILS, THE SLOT SAYS SO — IT DOES NOT GO QUIET
 *
 * `attention` rides on `GET /overview/activity`, the heaviest of the page's
 * three reads. The first mount was `activity.data !== null && <AttentionBlock>`,
 * which made a failed activity read and a workspace with nothing stopped render
 * **the same nothing** — and the chart card below it then told the reader
 * *"Nothing else on this page came from that request"*, which was false about
 * the one block whose job is to be noticed. A campaign halted half-way through a
 * list, on the day the charts timed out, was invisible and the page said nothing
 * was missing.
 *
 * So {@link AttentionSlot} owns the three states. Empty is still silence,
 * because empty is a fact. Unknown is a line saying Wend could not check, with a
 * retry and the way to check by hand — in `warn`, not `bad`, because nothing is
 * known to have stopped; what is known is that nobody looked. Loading draws
 * nothing, since the charts beside it already say they are loading and a
 * spinner in the most urgent slot on the page would read as an alarm.
 * Rejected: moving `attention` onto `GET /overview`, which would keep it off the
 * failing read — that response is `.strict()`, its header argues against a new
 * member at length, and a member there changes what every API suite asserts
 * about it. The slot is the presentation half of the fix and is enough to stop
 * the silence; the move is worth reconsidering if the activity read fails often.
 */
import {
  OVERVIEW_ATTENTION_LIMIT,
  type OverviewActivityResponse,
  type OverviewAttention,
  type StoppedCampaign,
} from '@wend/shared';
import { Badge, Button, Callout, Card, CardContent, cn } from '@wend/ui';
import { ArrowRight, OctagonAlert, RotateCw } from 'lucide-react';
import { Link } from 'react-router-dom';

import { errorMessage, isApiError } from '../../lib/api/errors';
import { CHANNEL_LABEL } from '../../lib/campaign-display';
import { formatCount, formatDateTime } from '../../lib/format';
import type { AsyncData } from '../../lib/use-async-data';

function StoppedRow({ campaign }: { campaign: StoppedCampaign }) {
  return (
    <li className="border-t border-bad/20 py-3 first:border-t-0 first:pt-0 last:pb-0">
      <div className="flex flex-col gap-1.5 sm:flex-row sm:items-baseline sm:justify-between sm:gap-6">
        <Link
          to={`/campaigns/${campaign.id}`}
          className="group flex min-w-0 items-center gap-1.5 text-[0.95rem] font-medium text-text underline-offset-4 hover:underline"
        >
          <span className="truncate">{campaign.name}</span>
          <ArrowRight
            aria-hidden="true"
            className="size-3.5 shrink-0 text-muted transition-transform group-hover:translate-x-0.5"
          />
        </Link>
        <span className="shrink-0 text-[0.76rem] text-muted">
          {CHANNEL_LABEL[campaign.channel]}
          {/* Null means nothing recorded the instant. It prints as nothing
              rather than as a fabricated timestamp — the same rule
              `OverviewSnapshot.checkedAt` follows. */}
          {campaign.stoppedAt !== null && <> · {formatDateTime(campaign.stoppedAt)}</>}
        </span>
      </div>
      <p className="mt-1 text-[0.82rem] leading-relaxed text-muted">{campaign.summary}</p>
    </li>
  );
}

export function AttentionBlock({
  attention,
  className,
}: {
  attention: OverviewAttention;
  className?: string;
}) {
  if (attention.total === 0) return null;

  const listed = attention.campaigns.length;
  const hidden = attention.total - listed;

  return (
    <Card className={cn('border-bad/45 bg-bad/[0.06]', className)}>
      <CardContent className="flex flex-col gap-4 p-[18px]">
        <div className="flex flex-wrap items-center gap-3">
          <OctagonAlert aria-hidden="true" className="size-4 shrink-0 text-bad" />
          <h2 className="text-[1.05rem] font-semibold tracking-[-0.02em] text-text">
            {attention.total === 1
              ? 'A campaign has stopped and is waiting for you'
              : `${formatCount(attention.total)} campaigns have stopped and are waiting for you`}
          </h2>
          <Badge tone="bad">Needs a decision</Badge>
        </div>

        <ul className="flex flex-col">
          {attention.campaigns.map((campaign) => (
            <StoppedRow key={campaign.id} campaign={campaign} />
          ))}
        </ul>

        {/* The cap is small on purpose — this block sits above everything and
            its job is to be noticed, and twenty rows is a thing people scroll
            past. `total` is the uncapped truth, so the overflow is named rather
            than hidden. */}
        {hidden > 0 && (
          <p className="text-[0.8rem] leading-relaxed text-muted">
            Showing {formatCount(listed)} of {formatCount(attention.total)} — at most{' '}
            {formatCount(OVERVIEW_ATTENTION_LIMIT)} fit here.{' '}
            <Link to="/campaigns" className="underline underline-offset-2 hover:text-text">
              See all campaigns
            </Link>
            .
          </p>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * The block's place on the dashboard, over the read that carries it — see "WHEN
 * THE READ THAT CARRIES IT FAILS" in the header.
 *
 * Data first, whatever the status: a failed *refresh* still has the last answer,
 * and a halted campaign from that answer is still worth showing.
 */
export function AttentionSlot({
  activity,
  className,
}: {
  activity: AsyncData<OverviewActivityResponse>;
  className?: string;
}) {
  if (activity.data !== null) {
    return <AttentionBlock attention={activity.data.attention} className={className} />;
  }

  if (activity.status !== 'error') return null;

  /*
   * SLICE 66: A WARNING CALLOUT, COMPACT, AND NOT A CARD.
   *
   * This was a warn-tinted `Card` holding an `ErrorState`. The Blueprint's
   * warning finding is one shape everywhere — a compact amber `Callout` with a
   * cream title — and this is a warning (the page cannot say whether something
   * stopped), not a panel of content. It keeps everything `ErrorState` gave it:
   * the request's own message, its request id, a retry, and the way to check by
   * hand. The title moved from `ErrorState`'s red mono line into the callout's
   * title, because one fault with two headings — a red one and an amber frame —
   * said "error" and "warning" about the same sentence.
   *
   * It announces itself, once: `live="assertive"` is the `role="alert"` the
   * `ErrorState` carried, and the chart cards that repeat this failure stay
   * quiet (verify-dashboard counts exactly one alert across the three).
   */
  const requestId = isApiError(activity.error) ? activity.error.requestId : undefined;

  return (
    <Callout
      tone="warn"
      density="compact"
      live="assertive"
      data-state="attention-unknown"
      className={className}
      title="Could not check for stopped campaigns"
      action={
        <Button
          type="button"
          variant="secondary"
          size="sm"
          leadingIcon={<RotateCw aria-hidden="true" />}
          onClick={activity.reload}
        >
          Try again
        </Button>
      }
    >
      <p>{errorMessage(activity.error)}</p>
      {requestId !== undefined && <p className="font-mono text-[0.7rem]">Request {requestId}</p>}
      <p className="mt-1">
        A campaign that stopped itself, or that is paused because credits ran out, would be listed
        here, and this page cannot say whether one is.{' '}
        <Link to="/campaigns" className="text-text underline underline-offset-2">
          Check Campaigns
        </Link>{' '}
        to see for yourself.
      </p>
    </Callout>
  );
}
